/**
 * 前后端业务失败的统一解析入口。
 * 后端约定：`{ success:false, error:{ code, message, details, retryable } }` + HTTP status。
 */

export type ApiErrorKind = 'offline' | 'timeout' | 'generic';

export type ParsedApiError = {
  kind: ApiErrorKind;
  code?: string;
  message: string;
  retryable: boolean;
  status?: number;
};

export class ApiClientError extends Error {
  readonly kind: ApiErrorKind;
  readonly code?: string;
  readonly retryable: boolean;
  readonly status?: number;

  constructor(parsed: ParsedApiError) {
    super(parsed.message);
    this.name = 'ApiClientError';
    this.kind = parsed.kind;
    this.code = parsed.code;
    this.retryable = parsed.retryable;
    this.status = parsed.status;
  }
}

type ErrorBody = {
  error?: { code?: string; message?: string; retryable?: boolean };
  code?: string;
  message?: string;
  detail?: string | { message?: string; code?: string };
} | null;

function readCode(body: ErrorBody): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  return (
    body.error?.code ||
    body.code ||
    (typeof body.detail === 'object' ? body.detail?.code : undefined)
  );
}

function readMessage(body: ErrorBody): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  if (body.error?.message) return body.error.message;
  if (typeof body.message === 'string' && body.message && body.message !== 'ok') {
    return body.message;
  }
  if (typeof body.detail === 'string') return body.detail;
  if (body.detail && typeof body.detail === 'object' && body.detail.message) {
    return body.detail.message;
  }
  return undefined;
}

function kindFromCodeOrStatus(code?: string, status?: number): ApiErrorKind | null {
  if (
    code === 'INFERENCE_OFFLINE' ||
    status === 503
  ) {
    return 'offline';
  }
  if (
    code === 'ENRICH_TIMEOUT' ||
    code === 'DEEP_TIMEOUT' ||
    code === 'INFERENCE_TIMEOUT' ||
    status === 408
  ) {
    return 'timeout';
  }
  return null;
}

function defaultMessage(kind: ApiErrorKind, code?: string, fallback?: string): string {
  if (kind === 'offline') return '大模型服务暂时不可用或已掉线，请稍后重试';
  if (kind === 'timeout') {
    if (code === 'DEEP_TIMEOUT') return '等待深度挖掘结果超时，请稍后重试';
    if (code === 'INFERENCE_TIMEOUT') return '大模型已连通，但未在时限内返回结果，请稍后重试';
    return '等待初步分析结果超时，请稍后重试';
  }
  return fallback || '请求失败，请稍后重试';
}

/** 统一解析业务接口失败响应（或裸 status）。 */
export function parseApiError(
  body: unknown,
  status?: number,
  fallback = '请求失败，请稍后重试',
): ParsedApiError {
  const b = body as ErrorBody;
  const code = readCode(b);
  const kind = kindFromCodeOrStatus(code, status) ?? 'generic';
  const fromBody = readMessage(b);
  const retryable =
    typeof b?.error?.retryable === 'boolean'
      ? b.error.retryable
      : kind === 'offline' || kind === 'timeout';

  if (!b || typeof b !== 'object') {
    const k = kindFromCodeOrStatus(undefined, status) ?? 'generic';
    return {
      kind: k,
      message: defaultMessage(k, undefined, fallback),
      retryable: k !== 'generic',
      status,
    };
  }

  return {
    kind,
    code,
    message: fromBody || defaultMessage(kind, code, fallback),
    retryable,
    status,
  };
}

/** 供 fetch 失败分支：抛出带 kind 的 ApiClientError。 */
export function throwApiError(body: unknown, status: number, fallback: string): never {
  throw new ApiClientError(parseApiError(body, status, fallback));
}

/** 兼容旧调用：只取可读文案。 */
export function apiErrorMessage(body: unknown, fallback: string, status?: number): string {
  return parseApiError(body, status, fallback).message;
}

/**
 * 网络层失败识别：浏览器 fetch 在 DNS 失败、连接被重置、代理断流时抛 TypeError，
 * 与业务错误响应无关（此时拿不到任何 envelope）。
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const m = error.message || '';
  return /Failed to fetch|NetworkError|Load failed|fetch failed|网络/i.test(m);
}

/** 把网络层失败包装成带 kind 的错误，供 UI 归类与提示。 */
export function toNetworkApiError(message = '网络连接中断，请检查网络后重试'): ApiClientError {
  return new ApiClientError({
    kind: 'offline',
    code: 'NETWORK_ERROR',
    message,
    retryable: true,
  });
}

/** 从文案或 ApiClientError 推断展示类型。 */
export function classifyClientError(error: unknown): ApiErrorKind {
  if (error instanceof ApiClientError) return error.kind;
  if (isNetworkError(error)) return 'offline';
  const m = error instanceof Error ? error.message : String(error || '');
  if (/超时|ENRICH_TIMEOUT|DEEP_TIMEOUT|INFERENCE_TIMEOUT|408/.test(m)) return 'timeout';
  if (/掉线|不可用|INFERENCE_OFFLINE|503/.test(m)) return 'offline';
  return 'generic';
}

/** 取用户可读文案。 */
export function clientErrorMessage(error: unknown, fallback = '请求失败，请稍后重试'): string {
  if (error instanceof ApiClientError) return error.message;
  // 浏览器原生 `Failed to fetch` 对用户无意义，统一换成可操作提示
  if (isNetworkError(error)) return '网络连接中断，请检查网络后重试';
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return fallback;
}
